import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  Easing,
  FadeIn,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import { ScheduleCalendar } from '@/components/schedule/schedule-calendar';
import { AvailabilityDay, AvailabilitySlot } from '@/types/appointment';
import {
  formatDayLabelLong,
  formatDuration,
  formatSlotTime,
  zonedDateKey,
  zonedMinutesOfDay,
} from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

const DEFAULT_BRAND = '#FD5001';

/** Coluna da hora na timeline; fixa para todas as linhas ficarem alinhadas. */
const HOUR_COLUMN = 46;
const SLOT_HEIGHT = 52;
const EMPTY_HOUR_HEIGHT = 26;

interface Props {
  days: AvailabilityDay[];
  brandColor?: string | null;
  selectedStart: string | null;
  onSelect: (slot: AvailabilitySlot) => void;
  /** Mostra "N vagas" nos slots (serviços de turma). */
  showCapacity?: boolean;
  /** Dias que já vão ficar marcados (ex.: grade recorrente escolhida). */
  markedDates?: Set<string>;
  /**
   * Outros horários (ISO) pintados como escolhidos além de `selectedStart` —
   * ex.: toda ocorrência de uma grade que repete, em qualquer dia que abrir.
   */
  highlightedStarts?: Set<string>;
}

interface HourRow {
  hour: number;
  slots: AvailabilitySlot[];
}

function slotMinutes(slot: AvailabilitySlot): number {
  return Math.round((new Date(slot.endAt).getTime() - new Date(slot.startAt).getTime()) / 60000);
}

interface HourRowViewProps {
  row: HourRow;
  brand: string;
  isActive: (startAt: string) => boolean;
  expanded: boolean;
  onToggle: () => void;
  onSelect: (slot: AvailabilitySlot) => void;
  showCapacity?: boolean;
}

/**
 * Uma hora da timeline. O cabeçalho fica sempre estático (sem morph, não estica);
 * só o bloco de cards abre/fecha animando a altura medida — cresce e encolhe de
 * verdade nos dois sentidos, com o conteúdo medido por baixo (position:absolute)
 * e recortado por overflow-hidden.
 */
function HourRowView({
  row,
  brand,
  isActive,
  expanded,
  onToggle,
  onSelect,
  showCapacity,
}: HourRowViewProps) {
  const th = useThemedHex();
  const hasActive = row.slots.some((s) => isActive(s.startAt));

  const [contentHeight, setContentHeight] = useState(0);
  const progress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    progress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 260 : 220,
      easing: Easing.inOut(Easing.ease),
    });
  }, [expanded, progress]);

  const collapseStyle = useAnimatedStyle(() => ({
    height: progress.value * contentHeight,
    opacity: progress.value,
  }));

  return (
    <View>
      {/* Cabeçalho da hora — abre/fecha os horários */}
      <PressableScale haptic onPress={onToggle} className="flex-row items-center">
        <View style={{ width: HOUR_COLUMN }} className="items-end pr-3 pt-1">
          <ThemedText
            className="font-semibold text-xs"
            style={{ color: hasActive ? brand : '#737373' }}
          >
            {String(row.hour).padStart(2, '0')}h
          </ThemedText>
        </View>
        <View className="flex-1 flex-row items-center justify-between border-l border-neutral-200 py-2 pl-3">
          {expanded ? (
            <ThemedText className="text-xs text-neutral-400">
              {row.slots.length} {row.slots.length === 1 ? 'horário' : 'horários'}
            </ThemedText>
          ) : (
            <AnimatedView
              entering={FadeIn.duration(180)}
              className="flex-1 flex-row flex-wrap gap-1.5 pr-2"
            >
              {row.slots.map((slot) => {
                const active = isActive(slot.startAt);
                return (
                  <View
                    key={slot.startAt}
                    className="h-6 justify-center rounded-full px-2.5"
                    style={{
                      backgroundColor: active ? brand : th('#F5F5F5'),
                      borderWidth: 1,
                      borderColor: active ? brand : '#EAEAEA',
                      borderCurve: 'continuous',
                    }}
                  >
                    <ThemedText
                      className="font-semibold text-[11px]"
                      style={{ color: active ? '#fff' : th('#525252') }}
                    >
                      {formatSlotTime(slot.startAt)}
                    </ThemedText>
                  </View>
                );
              })}
            </AnimatedView>
          )}
          <Ionicons className="text-neutral-400" name={expanded ? 'chevron-up' : 'chevron-down'} size={16} />
        </View>
      </PressableScale>

      {/* Cards dos horários — altura animada nos dois sentidos. Conteúdo medido
          por baixo (absolute) para funcionar mesmo começando recolhido. */}
      <AnimatedView style={[{ overflow: 'hidden' }, collapseStyle]}>
        <View
          style={{ position: 'absolute', left: 0, right: 0 }}
          onLayout={(e) => {
            const h = e.nativeEvent.layout.height;
            if (h > 0 && Math.abs(h - contentHeight) > 0.5) setContentHeight(h);
          }}
          className="flex-row"
        >
          <View style={{ width: HOUR_COLUMN }} />
          <View className="flex-1 gap-2 border-l border-neutral-200 pb-2 pl-3 pt-1">
            {row.slots.map((slot) => {
              const active = isActive(slot.startAt);
              return (
                <PressableScale
                  key={slot.startAt}
                  haptic
                  onPress={() => onSelect(slot)}
                  className="flex-row items-center gap-3 rounded-2xl px-3.5"
                  style={{
                    height: SLOT_HEIGHT,
                    backgroundColor: active ? brand : th('#fff'),
                    borderWidth: 1,
                    borderColor: active ? brand : th('#E5E5E5'),
                    borderCurve: 'continuous',
                  }}
                >
                  <ThemedText
                    className="font-bold text-base"
                    style={{ color: active ? '#fff' : th('#171717') }}
                  >
                    {formatSlotTime(slot.startAt)}
                  </ThemedText>
                  <ThemedText
                    className="flex-1 text-xs"
                    style={{ color: active ? 'rgba(255,255,255,0.85)' : th('#a3a3a3') }}
                  >
                    {formatDuration(slotMinutes(slot))}
                    {showCapacity && slot.remaining > 1 ? ` · ${slot.remaining} vagas` : ''}
                  </ThemedText>
                  <Ionicons
                    name={active ? 'checkmark-circle' : 'chevron-forward'}
                    size={active ? 20 : 16}
                    color={active ? '#fff' : th('#D4D4D4')}
                  />
                </PressableScale>
              );
            })}
          </View>
        </View>
      </AnimatedView>
    </View>
  );
}

/**
 * Seletor de dia + horário: calendário do mês para escolher o dia e timeline
 * do dia escolhido para escolher a hora. Apresentacional — recebe os dias já
 * calculados e devolve o slot. Reusado na página do serviço e na remarcação.
 */
export function AvailabilityPicker({
  days,
  brandColor,
  selectedStart,
  onSelect,
  showCapacity,
  markedDates,
  highlightedStarts,
}: Props) {
  const th = useThemedHex();
  const brand = brandColor || DEFAULT_BRAND;

  // Compara por instante: o ISO do preview e o da disponibilidade podem vir
  // formatados diferente para o mesmo horário.
  const isActive = useMemo(() => {
    const times = new Set([...(highlightedStarts ?? [])].map((s) => new Date(s).getTime()));
    return (startAt: string) => startAt === selectedStart || times.has(new Date(startAt).getTime());
  }, [selectedStart, highlightedStarts]);

  const availableDays = useMemo(
    () => days.filter((d) => d.available && d.slots.length > 0),
    [days],
  );
  const availableDates = useMemo(() => new Set(availableDays.map((d) => d.date)), [availableDays]);

  const todayKey = useMemo(() => zonedDateKey(new Date().toISOString()), []);

  const [activeDate, setActiveDate] = useState<string | null>(null);

  // Mantém um dia válido selecionado; segue o slot escolhido quando trocado.
  useEffect(() => {
    if (availableDays.length === 0) {
      setActiveDate(null);
      return;
    }
    const selectedDay = selectedStart
      ? availableDays.find((d) => d.slots.some((s) => s.startAt === selectedStart))
      : undefined;
    setActiveDate((current) => {
      if (selectedDay) return selectedDay.date;
      if (current && availableDays.some((d) => d.date === current)) return current;
      return availableDays[0].date;
    });
  }, [availableDays, selectedStart]);

  const activeSlots = useMemo(
    () => availableDays.find((d) => d.date === activeDate)?.slots ?? [],
    [availableDays, activeDate],
  );

  // Uma linha por hora entre o primeiro e o último horário do dia — as horas
  // sem vaga viram buracos visíveis na timeline.
  const hourRows = useMemo<HourRow[]>(() => {
    if (activeSlots.length === 0) return [];
    const byHour = new Map<number, AvailabilitySlot[]>();
    activeSlots.forEach((slot) => {
      const hour = Math.floor(zonedMinutesOfDay(slot.startAt) / 60);
      byHour.set(hour, [...(byHour.get(hour) ?? []), slot]);
    });
    const hours = [...byHour.keys()];
    const first = Math.min(...hours);
    const last = Math.max(...hours);
    return Array.from({ length: last - first + 1 }, (_, i) => {
      const hour = first + i;
      const slots = (byHour.get(hour) ?? []).sort((a, b) => a.startAt.localeCompare(b.startAt));
      return { hour, slots };
    });
  }, [activeSlots]);

  const bounds = useMemo(() => {
    const dates = availableDays.map((d) => d.date).sort();
    return { min: dates[0] ?? '', max: dates[dates.length - 1] ?? '' };
  }, [availableDays]);

  // Horas expandidas (cards abertos). Recolhido mostra só os chips condensados.
  const [expandedHours, setExpandedHours] = useState<Set<number>>(new Set());

  // Destaque lido por ref: tocar num horário (liberar/trocar) muda o destaque,
  // mas não pode fechar a hora que o usuário está olhando — só a troca de dia.
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Abre por padrão a hora do slot escolhido neste dia, senão a primeira com vaga.
  useEffect(() => {
    const withSlots = hourRows.filter((r) => r.slots.length > 0);
    if (withSlots.length === 0) {
      setExpandedHours(new Set());
      return;
    }
    const selectedHour = withSlots.find((r) =>
      r.slots.some((s) => isActiveRef.current(s.startAt)),
    )?.hour;
    setExpandedHours(new Set([selectedHour ?? withSlots[0].hour]));
  }, [hourRows]);

  const toggleHour = (hour: number) =>
    setExpandedHours((prev) => {
      const next = new Set(prev);
      if (next.has(hour)) next.delete(hour);
      else next.add(hour);
      return next;
    });

  if (availableDays.length === 0) return null;

  return (
    <View className="gap-4">
      <ScheduleCalendar
        availableDates={availableDates}
        selectedDate={activeDate}
        onSelectDate={setActiveDate}
        minDate={bounds.min}
        maxDate={bounds.max}
        todayKey={todayKey}
        brand={brand}
        markedDates={markedDates}
      />

      {/* Timeline do dia */}
      {!!activeDate && (
        <View className="gap-3">
          <View className="flex-row items-baseline justify-between">
            <ThemedText className="font-bold text-sm capitalize text-neutral-900">
              {activeDate === todayKey ? 'Hoje' : formatDayLabelLong(activeDate)}
            </ThemedText>
            <ThemedText className="text-xs text-neutral-400">
              {activeSlots.length} {activeSlots.length === 1 ? 'horário' : 'horários'}
            </ThemedText>
          </View>

          <View>
            {hourRows.map((row) => {
              const isEmpty = row.slots.length === 0;

              if (isEmpty) {
                return (
                  <View key={row.hour} className="flex-row">
                    <View style={{ width: HOUR_COLUMN }} className="items-end pr-3 pt-1">
                      <ThemedText className="font-semibold text-xs" style={{ color: th('#D4D4D4') }}>
                        {String(row.hour).padStart(2, '0')}h
                      </ThemedText>
                    </View>
                    <View className="flex-1 border-l border-neutral-200 pb-2 pl-3">
                      <View style={{ height: EMPTY_HOUR_HEIGHT }} className="justify-center">
                        <View className="h-px flex-1 self-stretch bg-neutral-100" />
                      </View>
                    </View>
                  </View>
                );
              }

              return (
                <HourRowView
                  key={row.hour}
                  row={row}
                  brand={brand}
                  isActive={isActive}
                  expanded={expandedHours.has(row.hour)}
                  onToggle={() => toggleHour(row.hour)}
                  onSelect={onSelect}
                  showCapacity={showCapacity}
                />
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
}
